/**
 * Maps a backend validation error message back onto the specific form
 * field it's about, so it can be shown inline (same as client-side
 * validation errors) instead of only as a generic toast.
 *
 * Why this exists: the backend's global exception filter
 * (clear-scan/src/common/filters/global-exception.filter.ts) flattens
 * class-validator errors down to a single string, in NestJS's default
 * format of "<field> <constraint text>" — e.g. "diagnosticCenterId must
 * be a string", "email must be a valid email". Before this, every form
 * just toasted that raw string, so a field-level error (something the
 * user can fix by looking at one input) got the same generic "something
 * went wrong" treatment as a real network/server failure — inconsistent
 * with how client-side validation errors are shown (inline, under the
 * field), and not actionable for the user (which field is it?).
 *
 * This is a best-effort parse, not a real structured error contract —
 * the backend only ever sends the first failing field's message anyway
 * (see the filter above), so this can't surface multiple simultaneous
 * field errors either. A real fix is to have the backend return
 * `{ field, message }[]`; tracked as a follow-up, not done here.
 */
export interface MappedApiError {
  /** The form field this error is about, if one could be identified. */
  field?: string;
  /** The message to show — inline under `field` if set, via toast otherwise. */
  message: string;
}

export function mapApiErrorToField(
  rawMessage: string,
  knownFields: readonly string[]
): MappedApiError {
  const firstWord = rawMessage.trim().split(/\s+/)[0];
  let field = knownFields.find(
    f => f.toLowerCase() === firstWord.toLowerCase()
  );

  // class-validator messages lead with the field name ("email must be a
  // valid email"), but a raw DB driver error names the column in quotes
  // instead ("Field 'image_main' doesn't have a default value", "Unknown
  // column 'foo' in 'field list'") - check for that shape too so a
  // leaked DB error still lands inline on the right field instead of
  // only as a generic toast, same as any other validation error.
  if (!field) {
    const quoted = rawMessage.match(/'([A-Za-z0-9_]+)'/);
    if (quoted) {
      field = knownFields.find(
        f => f.toLowerCase() === quoted[1].toLowerCase()
      );
    }
  }

  return { field, message: rawMessage };
}

/**
 * Convenience wrapper for the common case: given a caught error and the
 * form's known field names, returns which `setErrors` field to populate
 * (if any) and always returns a message to toast as well — so an error
 * on an unrecognized/removed field is never silently dropped.
 */
export function extractApiErrorMessage(err: unknown): string {
  return err instanceof Error
    ? err.message
    : 'Something went wrong. Please try again.';
}
